import {
  Button,
  Heading,
  AgGrid,
  Spinner,
  ErrorMessage,
  InfoPopup,
  MobileResponsiveGrid,
} from "@/common/components";
import { PrivateContext } from "@/routes/PrivateRoute";
import { useState, useContext, useEffect, useSelector } from "@/common/hooks";
import {
  useSaveWithdrawalElectionMutation,
  useGetOwnershipInquiryMutation,
  useSearchProductOwnershipMutation,
} from "@/service/OwnerService";
import {
  setEditProjectionsResponse,
  setProjectionsData,
  setSpouseDetailsInStore,
  setOwnershipInquiryResult,
  setAsOfBalanceAccountData,
  setHasSearchedForAsOfBalanceAccountData,
  setMoneySourceDetailsValues,
  setTransactionDetailsForDateRange,
  setSearchProductOwnershipResult,
  setSelectedOwner,
} from "@/store/ownersSlice";
import { amountFormatter, encryptData } from "@/utils/Utilities";
import dayjs from "dayjs";

const Projections = ({ accountDetails, clickedOwnerData }) => {
  const {
    labels,
    redirectTo,
    dispatch,
    productSponsorId,
    accessProfileId,
    accessorPrivileges,
  } = useContext(PrivateContext);

  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [projectionsRowData, setProjectionsRowData] = useState([]);
  const [checkboxState, setCheckboxState] = useState({
    elect1: false,
    elect2: false,
  });
  const [showElectionPopup, setShowElectionPopup] = useState(false);
  const [showCancelElectionPopup, setShowCancelElectionPopup] = useState(false);
  const [selectedOwnerDetails, setSelectedOwnerDetails] = useState({});
  const [ownerSearchDetails, setOwnerSearchDetails] = useState({});

  const handleElect1Change = () => {
    if (checkboxState?.elect1) {
      setCheckboxState({ elect1: false, elect2: false });
    } else {
      setCheckboxState({ elect1: true, elect2: false });
    }
  };

  const [saveWithdrawalElectionApi, { isLoading: isFetching1, error: error1 }] =
    useSaveWithdrawalElectionMutation();
  const [getOwnershipInquiryApi, { isLoading: isFetching2, error2 }] =
    useGetOwnershipInquiryMutation();
  const [searchProductOwnershipApi, { isLoading: isFetching3, error3 }] =
    useSearchProductOwnershipMutation();

  const getRowClass = (params) => {
    if (params.node.rowIndex === selectedRowIndex) {
      return "hover-bg";
    }
    return "";
  };

  const handleRemoveProjection = () => {
    setCheckboxState({ elect1: false, elect2: false });
    if (editProjectionsResponseFromStore) {
      dispatch(setEditProjectionsResponse(null));
      dispatch(setSpouseDetailsInStore(null));
    }
  };

  const editProjectionsResponseFromStore = useSelector(
    (state) => state?.ownersReducer?.editProjectionsResponse
  );
  const projectionsDataFromStore = useSelector(
    (state) => state?.ownersReducer?.projectionsData
  );
  const spouseDetailsFromStore = useSelector(
    (state) => state?.ownersReducer?.spouseDetails
  );
  const selectedOwnerFromStore = useSelector(
    (state) => state?.ownersReducer?.selectedOwner
  );
  const searchParamsFromStore = useSelector(
    (state) => state?.ownersReducer?.ownerSearchParams
  );

  const columnDefs = [
    {
      headerName: labels?.ownerInquiry?.ownerRetirementAge,
      field: "ownerRetirementAge",
    },
    {
      headerName: labels?.ownerInquiry?.elect,
      field: "elect1",
      // remove the built-in editor if you don't want it:
      // cellEditor: "agCheckboxCellEditor",
      editable: (params) => params.node.rowIndex === 0,
      cellRenderer: (params) => {
        const isDisabled =
          parseFloat(params?.data?.singleBenefit) === 0 ||
          selectedOwnerDetails?.ownerEligibilityCode !== "E" ||
          selectedOwnerDetails?.genderCode === "U" ||
          accessorPrivileges["LOCK IN ELECTION"] == "RETRIEVE";

        if (params.node.rowIndex !== 0) return null;

        return (
          <input
            key="elect1"
            type="checkbox"
            checked={checkboxState?.elect1}
            onChange={(e) => {
              // stop AG Grid from handling the click (selection/editing)
              e.stopPropagation();
              // update your local state / call handler
              handleElect1Change();
            }}
            onMouseDown={(e) => {
              // necessary in some AG Grid versions to prevent grid from focusing/starting edit
              e.stopPropagation();
            }}
            onClick={(e) => e.stopPropagation()}
            disabled={isDisabled}
          />
        );
      },
    },

    {
      headerName: labels?.ownerInquiry?.singleBenefit,
      field: "singleBenefit",
      cellRenderer: amountFormatter,
    },
    {
      headerName: labels?.ownerInquiry?.elect,
      field: "elect2",
      editable: (params) =>
        params.node.rowIndex === 0 &&
        (editProjectionsResponseFromStore
          ? editProjectionsResponseFromStore?.jointEligibilityCode
          : "") === "E",
      cellEditor: "agCheckboxCellEditor",
      cellRenderer: (params) => {
        const isEligible =
          editProjectionsResponseFromStore?.jointEligibilityCode === "E";
        const isChecked =
          isEligible &&
          spouseDetailsFromStore &&
          Object.keys(spouseDetailsFromStore).length > 0 &&
          checkboxState.elect2;

        return params.node.rowIndex === 0 ? (
          <input
            key="elect2"
            type="checkbox"
            checked={isChecked}
            onChange={() => redirectTo("projectionSpouseDetails")}
            disabled={!isEligible}
          />
        ) : null;
      },
    },
    {
      headerName: labels?.ownerInquiry?.spousalBenefit,
      field: "spousalBenefit",
      cellRenderer: amountFormatter,
    },
  ];

  const renderElect1Checkbox = (params) => {
    if (params?.rowIndex !== 0) return null;
    const isDisabled =
      parseFloat(params?.data?.singleBenefit) === 0 ||
      selectedOwnerDetails?.ownerEligibilityCode !== "E" ||
      selectedOwnerDetails?.genderCode === "U";
    return (
      <input
        key="elect1Mobile"
        type="checkbox"
        checked={checkboxState?.elect1}
        onChange={handleElect1Change}
        disabled={isDisabled}
      />
    );
  };
  const renderElect2Checkbox = (params) => {
    if (params.rowIndex !== 0) return null;
    return (
      <input
        key="elect2Mobile"
        type="checkbox"
        checked={checkboxState?.elect2}
        onChange={() => redirectTo("projectionSpouseDetails")}
        disabled={
          editProjectionsResponseFromStore?.jointEligibilityCode === "E"
            ? false
            : true
        }
      />
    );
  };
  const columnDefsMobile = [
    {
      headerName: labels?.ownerInquiry?.ownerRetirementAge,
      field: "ownerRetirementAge",
    },
    {
      headerName: labels?.ownerInquiry?.elect,
      field: "elect1",
      cellRenderer: renderElect1Checkbox,
    },
    {
      headerName: labels?.ownerInquiry?.singleBenefit,
      field: "singleBenefit",
      cellRenderer: amountFormatter,
    },
    {
      headerName: labels?.ownerInquiry?.elect,
      field: "elect2",
      cellRenderer: renderElect2Checkbox,
    },
    {
      headerName: labels?.ownerInquiry?.spousalBenefit,
      field: "spousalBenefit",
      cellRenderer: amountFormatter,
    },
  ];

  const defaultColDef = {
    suppressSizeToFit: true,
    resizable: false,
    sortable: false,
    flex: 1,
    suppressMovable: true,
    wrapHeaderText: true,
    autoHeaderHeight: true,
    wrapText: true,
    autoHeight: true,
  };

  const searchProductOwnerships = async () => {
    const ownerSearchPayload = {
      searchType: ownerSearchDetails?.searchType,
      searchKeyword:
        ownerSearchDetails?.searchType === "T"
          ? await encryptData(ownerSearchDetails?.searchKeyword)
          : ownerSearchDetails?.searchKeyword,
      planId: ownerSearchDetails?.planId,
      productSponsorId: productSponsorId,
      accessProfileId: accessProfileId,
    };
    searchProductOwnershipApi(ownerSearchPayload)
      .unwrap()
      .then((res) => {
        if (!res?.errors?.length) {
          const searchResults = res?.productOwnerships;
          dispatch(setSearchProductOwnershipResult(res?.productOwnerships));

          const filteredOwner = searchResults?.find(
            (owner) =>
              owner?.maskedOwnerTaxId ===
                selectedOwnerDetails?.maskedOwnerTaxId &&
              owner?.ownershipId === selectedOwnerDetails?.ownershipId
          );
          dispatch(setSelectedOwner(filteredOwner));
          inquiryOwnershipSearch();
        }
      });
  };

  const inquiryOwnershipSearch = async () => {
    const getOwnershipInquiryPayload = {
      lineOfBusinessId: selectedOwnerDetails?.lineOfBusinessId,
      ownershipTypeCode: selectedOwnerDetails?.ownershipType,
      ownerTaxId: selectedOwnerDetails?.ownerTaxId,
      planId: selectedOwnerDetails?.planId,
      externalProductId: selectedOwnerDetails?.product?.externalId,
      recordKeeperName:
        selectedOwnerDetails?.recordkeeperRecordkeepingSystem?.recordkeeperName,
      recordKeepingSystemName:
        selectedOwnerDetails?.recordkeeperRecordkeepingSystem
          ?.recordkeepingName,
      productSponsorId: productSponsorId,
      accessProfileId: accessProfileId,
    };

    getOwnershipInquiryApi(getOwnershipInquiryPayload)
      .unwrap()
      .then((res) => {
        if (!res?.errors?.length) {
          dispatch(setProjectionsData([]));
          dispatch(setOwnershipInquiryResult(res));
          dispatch(setAsOfBalanceAccountData(null));
          dispatch(setHasSearchedForAsOfBalanceAccountData(false));
          dispatch(setEditProjectionsResponse(null));
          dispatch(setMoneySourceDetailsValues(null));
          dispatch(setSpouseDetailsInStore(null));
          dispatch(setTransactionDetailsForDateRange(null));
        }
      });
  };

  const handleElectWithdrawal = async () => {
    const saveWithdrawalElectionPayload = {
      annuitantOwnerTaxId: checkboxState.elect2
        ? await encryptData(spouseDetailsFromStore?.spouseTaxId)
        : "",
      annuitantFirstName: checkboxState.elect2
        ? spouseDetailsFromStore?.firstName
        : "",
      annuitantMiddleName: checkboxState.elect2
        ? spouseDetailsFromStore?.middleName || ""
        : "",
      annuitantLastName: checkboxState.elect2
        ? spouseDetailsFromStore?.lastName
        : "",
      annuitantAddressLine1: checkboxState.elect2
        ? spouseDetailsFromStore?.addressLine1
        : "",
      annuitantAddressLine2: checkboxState.elect2
        ? spouseDetailsFromStore?.addressLine2
        : "",
      annuitantAddressLine3: checkboxState.elect2
        ? spouseDetailsFromStore?.addressLine3
        : "",
      annuitantCity: checkboxState.elect2 ? spouseDetailsFromStore?.city : "",
      annuitantState: checkboxState.elect2
        ? spouseDetailsFromStore?.statePostalCode
        : "",
      annuitantAddressTypeCode: checkboxState.elect2
        ? spouseDetailsFromStore?.annuitantAddressTypeCode
        : "",
      annuitantGenderCode: checkboxState.elect2
        ? spouseDetailsFromStore?.genderCode
        : "",
      annuitantUSResidencyCode: checkboxState.elect2 ? "Z" : "",
      annuitantEmailAddress: checkboxState.elect2
        ? spouseDetailsFromStore?.email
          ? spouseDetailsFromStore?.email
          : ""
        : "",
      annuitantZipCode: checkboxState.elect2
        ? spouseDetailsFromStore?.zipCode
        : "",
      annuitantPhoneNumber: checkboxState.elect2
        ? spouseDetailsFromStore?.phoneNumber
        : "",
      annuitantBirthDate: checkboxState.elect2
        ? spouseDetailsFromStore?.annuitantBirthDate
        : "",
      civilUnionCode: checkboxState.elect2
        ? spouseDetailsFromStore?.civilUnion
        : "",
      externalProductId: clickedOwnerData?.product?.externalId,
      lineOfBusinessId: clickedOwnerData?.lineOfBusinessId,
      recordKeeperName:
        clickedOwnerData?.recordkeeperRecordkeepingSystem?.recordkeeperName,
      recordKeepingSystemName:
        clickedOwnerData?.recordkeeperRecordkeepingSystem?.recordkeepingName,
      planId: clickedOwnerData?.planId,
      withDrawalElectionChoice:
        checkboxState.elect1 && !checkboxState.elect2 ? "S" : "J",
      ownershipTypeCode: clickedOwnerData?.ownershipType,
      ownerTaxId: clickedOwnerData?.ownerTaxId,
      productSponsorId: productSponsorId,
      accessProfileId: accessProfileId,
    };
    saveWithdrawalElectionApi(saveWithdrawalElectionPayload)
      .unwrap()
      .then((res) => {
        if (!res?.errors?.length) {
          searchProductOwnerships();
        }
      });
  };

  const handleElectWithdrawalCancellation = () => {
    const basePayload = {
      externalProductId: clickedOwnerData?.product?.externalId,
      lineOfBusinessId: clickedOwnerData?.lineOfBusinessId,
      recordKeeperName:
        clickedOwnerData?.recordkeeperRecordkeepingSystem?.recordkeeperName,
      recordKeepingSystemName:
        clickedOwnerData?.recordkeeperRecordkeepingSystem?.recordkeepingName,
      planId: clickedOwnerData?.planId,
      ownershipTypeCode: clickedOwnerData?.ownershipType,
      ownerTaxId: clickedOwnerData?.ownerTaxId,
      productSponsorId: productSponsorId,
      accessProfileId: accessProfileId,
    };
    saveWithdrawalElectionApi(basePayload)
      .unwrap()
      .then((res) => {
        if (!res?.errors?.length) {
          searchProductOwnerships();
          inquiryOwnershipSearch();
        }
      });
  };

  const currPeriodGuaranteeValue = [
    {
      label: labels?.ownerInquiry?.guaranteedWithdrawalAmount,
      value: amountFormatter(
        checkboxState.elect1
          ? accountDetails?.projectedMaximumAnnualWithdrawalAmount
          : projectionsRowData[0]?.spousalBenefit
      ),
    },
    {
      label: labels?.ownerInquiry?.firstYearWithdrawalAmount,
      value: amountFormatter(
        checkboxState.elect1
          ? accountDetails?.firstWithdrawalAmount
          : editProjectionsResponseFromStore?.firstWithdrawalAmount
      ),
    },
    {
      label: labels?.ownerInquiry?.electionType,
      value: checkboxState.elect1
        ? labels?.ownerInquiry?.singleBenefit
        : labels?.ownerInquiry?.spousalBenefit,
    },
    {
      label: labels?.ownerInquiry?.ownerDateOfBirth,
      value: dayjs(clickedOwnerData?.dateOfBirth).format("MM/DD/YYYY"),
    },
    checkboxState.elect2 && {
      label: labels?.ownerInquiry?.spouseDateOfBirth,
      value: editProjectionsResponseFromStore?.jointAnnuitantBirthDate,
    },
  ].filter(Boolean);

  useEffect(() => {
    if (projectionsDataFromStore) {
      setProjectionsRowData(projectionsDataFromStore);
    }
  }, [projectionsDataFromStore]);

  useEffect(() => {
    if (searchParamsFromStore) {
      let ownerSearchParams = {
        searchKeyword: searchParamsFromStore?.searchKeyword,
        searchType: searchParamsFromStore?.searchType,
        planId: searchParamsFromStore?.planId,
      };
      setOwnerSearchDetails(ownerSearchParams);
    }
  }, [searchParamsFromStore]);

  useEffect(() => {
    if (selectedOwnerFromStore) {
      setSelectedOwnerDetails(selectedOwnerFromStore);
    }
  }, [selectedOwnerFromStore]);

  useEffect(() => {
    if (spouseDetailsFromStore) {
      setCheckboxState({ elect1: false, elect2: true });
    }
  }, [spouseDetailsFromStore]);

  useEffect(() => {
    const rows = [];

    if (
      accountDetails?.guaranteedBenefitsAge &&
      accountDetails?.projectedMaximumAnnualWithdrawalAmount
    ) {
      rows.push({
        ownerRetirementAge:
          accountDetails.guaranteedBenefitsAge ===
          accountDetails.productOwnerAge
            ? `${accountDetails.guaranteedBenefitsAge} (Current)`
            : accountDetails.guaranteedBenefitsAge,
        elect1: false,
        singleBenefit: accountDetails.projectedMaximumAnnualWithdrawalAmount,
        elect2: false,
        spousalBenefit: null,
      });
    }
    if (
      accountDetails?.guaranteedBenefitsAge2 &&
      accountDetails?.projectedMaximumAnnualWithdrawalAmount2
    ) {
      rows.push({
        ownerRetirementAge: accountDetails.guaranteedBenefitsAge2,
        elect1: false,
        singleBenefit: accountDetails.projectedMaximumAnnualWithdrawalAmount2,
        elect2: false,
        spousalBenefit: null,
      });
    }
    if (
      accountDetails?.guaranteedBenefitsAge3 &&
      accountDetails?.projectedMaximumAnnualWithdrawalAmount3
    ) {
      rows.push({
        ownerRetirementAge: accountDetails.guaranteedBenefitsAge3,
        elect1: false,
        singleBenefit: accountDetails.projectedMaximumAnnualWithdrawalAmount3,
        elect2: false,
        spousalBenefit: null,
      });
    }

    if (editProjectionsResponseFromStore) {
      if (rows[0]) {
        rows[0].spousalBenefit =
          editProjectionsResponseFromStore?.jointProjectedMAWAmount;
      }
      if (rows[1]) {
        rows[1].spousalBenefit =
          editProjectionsResponseFromStore?.jointProjectedMAWAmount2;
      }
      if (rows[2]) {
        rows[2].spousalBenefit =
          editProjectionsResponseFromStore?.jointProjectedMAWAmount3;
      }

      if (editProjectionsResponseFromStore?.hypotheticalOwnerAge) {
        rows.push({
          ownerRetirementAge:
            editProjectionsResponseFromStore?.hypotheticalOwnerAge,
          elect1: false,
          singleBenefit:
            editProjectionsResponseFromStore?.hypotheticalProjectedMAWAmount,
          elect2: false,
          spousalBenefit:
            editProjectionsResponseFromStore?.hypotheticalJointProjectedMAWAmount,
        });
      }
    }
    dispatch(setProjectionsData(rows));
  }, [accountDetails, editProjectionsResponseFromStore, clickedOwnerData]);

  if (!projectionsRowData?.length) return null;

  const handleUpdateElectionBtn = () => {
    if (
      selectedOwnerDetails.genderCode == "U" ||
      selectedOwnerDetails.ownerEligibilityCode == "N" ||
      accessorPrivileges["LOCK IN ELECTION"] == "RETRIEVE"
    ) {
      return false;
    } else {
      return true;
    }
  };

  return (
    <>
      {(isFetching1 || isFetching2 || isFetching3) && <Spinner />}
      <ErrorMessage error={error1 || error2 || error3} />
      {showElectionPopup && (
        <InfoPopup
          heading={labels?.ownerInquiry?.withdrawalElectionConfirmation}
          showModal={showElectionPopup}
          primaryButtonText={labels?.ownerInquiry?.confirm}
          primaryButtonClick={() => {
            setShowElectionPopup(false);
            handleElectWithdrawal();
          }}
          secondaryButtonText={labels?.ownerInquiry?.cancel}
          secondaryButtonClick={() => {
            setShowElectionPopup(false);
          }}
          body={
            <div className="mt-5 text-start">
              {currPeriodGuaranteeValue?.map((data) => (
                <p className="mb-1" key={data.label}>
                  {data?.label}: {data?.value}
                </p>
              ))}
            </div>
          }
        />
      )}
      {showCancelElectionPopup && (
        <InfoPopup
          heading={labels?.ownerInquiry?.withdrawalElectionCancellation}
          showModal={showCancelElectionPopup}
          primaryButtonText={labels?.ownerInquiry?.done}
          primaryButtonClick={() => {
            handleElectWithdrawalCancellation();
            setShowCancelElectionPopup(false);
          }}
          secondaryButtonText={labels?.ownerInquiry?.cancel}
          secondaryButtonClick={() => {
            setShowCancelElectionPopup(false);
          }}
          body={
            <p className="mt-5 text-start">
              {labels?.ownerInquiry?.withdrawalElectionCancellationMessage}
            </p>
          }
        />
      )}
      <Heading
        text={labels?.ownerInquiry?.guaranteedProjections}
        wrapperClassNames="h4 border-bottom-1 pb-1 my-1"
        title={false}
      />
      <div className="d-flex flex-row mt-3 mb-3 align-items-center gap-1">
        <Button
          text={labels?.ownerInquiry?.editProjections}
          wrapperClassName="col-md-3 col-6"
          click={() => redirectTo("projectionSpouseDob")}
          disabled={
            projectionsRowData?.length === 0 ||
            clickedOwnerData?.pendingStatusCode === "Y"
          }
        />
        <Button
          text={labels?.ownerInquiry?.removeProjection}
          wrapperClassName="col-md-3 col-6"
          click={handleRemoveProjection}
          disabled={!editProjectionsResponseFromStore}
        />
      </div>
      <div className="grid-detail-box border-bottom-1 border-top mb-3">
        <Button
          text={labels?.ownerInquiry?.updateSpouse}
          type="secondary"
          wrapperClassName="col-lg-3 col-md-4"
          click={() => redirectTo("projectionSpouseDetails")}
          disabled={!spouseDetailsFromStore || checkboxState.elect1}
        />
      </div>
      <AgGrid
        rowData={projectionsRowData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        domLayout="normal"
        suppressClickEdit={true}
        getRowClass={getRowClass}
        onRowClicked={(event) => setSelectedRowIndex(event.node.rowIndex)}
        wrapperClassNames="d-none d-md-block"
      />
      <MobileResponsiveGrid
        key="projections"
        rowData={projectionsRowData}
        columnDefs={columnDefsMobile}
        itemsPerPage={4}
        showPagination={false}
      />
      <div className="d-flex flex-column-reverse flex-md-row mt-3 column-gap-1 row-gap-1 align-items-center justify-content-end">
        <Button
          text={labels?.ownerInquiry?.done}
          type="primary"
          wrapperClassName="col-12 col-md-2"
          click={() => setShowElectionPopup(true)}
          disabled={
            (!checkboxState.elect1 && !checkboxState.elect2) ||
            clickedOwnerData?.pendingStatusCode === "Y" ||
            !handleUpdateElectionBtn()
          }
        />
        {clickedOwnerData?.pendingStatusCode === "Y" && (
          <Button
            text={labels?.ownerInquiry?.cancelElectionToWithdraw}
            type="secondary"
            wrapperClassName="col-12 col-md-4"
            click={() => setShowCancelElectionPopup(true)}
          />
        )}
      </div>
      <div className="border-top py-4 my-5">
        <p className="fw-bold">{labels?.ownerInquiry?.disclaimerTitle}</p>
        <span className="fw-light">
          {labels?.ownerInquiry?.disclaimerContent}
        </span>
      </div>
    </>
  );
};

export default Projections;
